import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import InstallPrompt from './components/InstallPrompt';
import PinGate from './components/PinGate';
import AdminAuthGate from './components/AdminAuthGate';
import SplashScreen from './components/SplashScreen';
import MenuPage from './pages/MenuPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ConfirmationPage from './pages/ConfirmationPage';
import OrderLookupPage from './pages/OrderLookupPage';
import AdminPage from './pages/AdminPage';
import CafeDashboard from './pages/CafeDashboard';
import KitchenPage from './pages/KitchenPage';
import PosPage from './pages/PosPage';
import PosSettingsPage from './pages/PosSettingsPage';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import ReceiptPage from './pages/ReceiptPage';
import PayPage from './pages/PayPage';
import GpTestPage from './pages/GpTestPage';
import StaffLauncherPage from './pages/StaffLauncherPage';
import RosePage from './pages/RosePage';
import { isNativeApp } from './utils/platform';
import { initNativePush } from './utils/nativePush';
import './App.css';

if (isNativeApp()) {
  initNativePush().catch((e) => console.error('Native push init error:', e));
}

function AuthGate({ children }) {
  const { pathname } = useLocation();
  const { isLoggedIn, loading } = useAuth();
  const [dismissed, setDismissed] = useState(() => !!sessionStorage.getItem('auth-gate-dismissed'));

  // Staff dashboards / launcher use PIN auth only — do not require a customer account or guest dismiss.
  if (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/cafe') ||
    pathname.startsWith('/kitchen') ||
    pathname.startsWith('/pos') ||
    pathname === '/staff' ||
    pathname === '/rose' ||
    pathname.startsWith('/rose/') ||
    pathname === '/barb' ||
    pathname.startsWith('/barb/') ||
    pathname === '/nouf' ||
    pathname.startsWith('/nouf/')
  ) {
    return children;
  }

  // Password reset must work without signing in or choosing guest first.
  if (pathname === '/forgot-password' || pathname === '/reset-password' || pathname === '/verify-email') {
    return children;
  }

  // Token-authenticated routes — these carry their own auth in the URL
  // (paySecret / order tokens) and must work when opened in a fresh
  // SafariViewController context with no shared session.
  if (pathname.startsWith('/pay') || pathname.startsWith('/receipt')) {
    return children;
  }

  if (loading) return null;
  if (isLoggedIn || dismissed) return children;

  return (
    <div className="app">
      <AuthPage
        onGuestContinue={() => {
          sessionStorage.setItem('auth-gate-dismissed', '1');
          setDismissed(true);
        }}
      />
    </div>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    const path = window.location.pathname;
    if (
      path === '/rose' || path.startsWith('/rose/')
      || path === '/barb' || path.startsWith('/barb/')
      || path === '/nouf' || path.startsWith('/nouf/')
    ) return false;
    const shown = sessionStorage.getItem('splash-shown');
    return !shown;
  });

  const handleSplashFinished = useCallback(() => {
    sessionStorage.setItem('splash-shown', '1');
    setShowSplash(false);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
      <CartProvider>
        {showSplash && <SplashScreen onFinished={handleSplashFinished} />}
        <AuthGate>
        <div className="app">
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Header />
                  <InstallPrompt />
                  <MenuPage />
                </>
              }
            />
            <Route
              path="/cart"
              element={
                <>
                  <Header />
                  <CartPage />
                </>
              }
            />
            <Route
              path="/checkout"
              element={
                <>
                  <Header />
                  <CheckoutPage />
                </>
              }
            />
            <Route path="/confirmation/group/:groupId" element={<ConfirmationPage />} />
            <Route path="/confirmation/:orderNumber" element={<ConfirmationPage />} />
            <Route path="/pay" element={<PayPage />} />
            <Route path="/gp-screenshot-test" element={<GpTestPage />} />
            <Route path="/receipt/group/:groupId" element={<ReceiptPage />} />
            <Route path="/receipt/:orderNumber" element={<ReceiptPage />} />
            <Route
              path="/order/:orderNumber"
              element={
                <>
                  <Header />
                  <OrderLookupPage />
                </>
              }
            />
            <Route
              path="/auth"
              element={
                <>
                  <Header />
                  <AuthPage />
                </>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <>
                  <Header />
                  <ForgotPasswordPage />
                </>
              }
            />
            <Route
              path="/reset-password"
              element={
                <>
                  <Header />
                  <ResetPasswordPage />
                </>
              }
            />
            <Route
              path="/verify-email"
              element={
                <>
                  <Header />
                  <VerifyEmailPage />
                </>
              }
            />
            <Route
              path="/my-orders"
              element={
                <>
                  <Header />
                  <OrderHistoryPage />
                </>
              }
            />
            {/* Staff entry (triple-tap logo). Destination pages keep their own PinGate. */}
            <Route path="/staff" element={<StaffLauncherPage />} />
            <Route path="/rose" element={<RosePage />} />
            {/* POS routes stay available for the dedicated pos-app Capacitor shell.
                Not linked from the customer staff launcher — POS remains a separate app. */}
            <Route path="/pos/settings" element={<PinGate type="counter"><PosSettingsPage /></PinGate>} />
            <Route path="/pos" element={<PinGate type="counter"><PosPage /></PinGate>} />
            {/* Cafe and admin keep a PIN. Kitchen screens are public sales counts, same as /rose. */}
            <Route path="/cafe" element={<PinGate type="cafe"><CafeDashboard /></PinGate>} />
            <Route path="/nouf" element={<KitchenPage kitchen="st_noufer" />} />
            <Route path="/barb" element={<KitchenPage kitchen="st_barbara" />} />
            <Route path="/kitchen/st-noufer" element={<Navigate to="/nouf" replace />} />
            <Route path="/kitchen/st-barbara" element={<Navigate to="/barb" replace />} />
            <Route path="/kitchen" element={<KitchenPage />} />
            <Route path="/admin" element={<AdminAuthGate><AdminPage /></AdminAuthGate>} />
          </Routes>
        </div>
        </AuthGate>
      </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
